import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ImageIcon, Pencil, Trash2 } from "lucide-react";
import { useRuntime } from "../../react/runtime/RuntimeContext";

// ImageField — read/edit widget for kernel `fields.Image`. Renders a
// square thumbnail tile. Edit mode adds pencil (upload/replace) +
// trash (clear) icon-buttons at the bottom corners.
//
// Backend contract (foundation.document.attachments controller):
//   POST   /api/document/attachments?model_key=&record_uuid=
//                                    &field_name=&usage_kind=field_image
//                                    &filename=&mimetype=
//          body = raw bytes, Content-Type = the file's mimetype
//          → { success: true, attachment: { record_uuid: "<new-uuid>" } }
//   GET    /api/document/attachments/{uuid}/preview
//          → image bytes (bearer-authenticated; consumed as Blob)
//
// The field value on the form record is a string uuid (the attachment's
// record_uuid). The form sheet writes the new uuid via `onChange` after
// upload; the user has to save the form for the link to persist.
// Upload is gated on (modelKey, recordUuid) both being set — a brand-new
// unsaved record can't host an attachment yet (no FK target).
//
// Ported from `src/frontend/src/workspace/views/fields/ImageField.tsx`
// (legacy frontend). Differences from the legacy:
//   - uses `runtime.http.getBlob` / `runtime.http.postBytes` (the
//     primitives shipped alongside this port) instead of a direct
//     `httpClient.getBlob` import + raw `fetch` for upload.
//   - no longer extends a base FieldComponent class; a plain React
//     FC for read mode + edit mode shared between them.

export type ImageFieldSize = "sm" | "md" | "lg" | "xl" | "xxl";

const TILE_CLASSES: Record<ImageFieldSize, string> = {
    sm:  "w-16 h-16",
    md:  "w-24 h-24",
    lg:  "w-32 h-32",
    xl:  "w-40 h-40",
    xxl: "w-48 h-48",
};

const ICON_CLASSES: Record<ImageFieldSize, string> = {
    sm:  "w-6 h-6",
    md:  "w-8 h-8",
    lg:  "w-10 h-10",
    xl:  "w-12 h-12",
    xxl: "w-16 h-16",
};

// Initials typography per tile size — keeps the abbreviation legible at
// every tile dimension (a fixed font would overflow `sm` or look lost on
// `xxl`). These are real Tailwind text-size utilities.
const INITIALS_CLASSES: Record<ImageFieldSize, string> = {
    sm:  "text-base",
    md:  "text-xl",
    lg:  "text-2xl",
    xl:  "text-3xl",
    xxl: "text-4xl",
};

export function resolveImageFieldSize(raw: unknown): ImageFieldSize {
    if (typeof raw !== "string") return "md";
    const v = raw.trim().toLowerCase();
    if (v === "sm" || v === "md" || v === "lg" || v === "xl" || v === "xxl") return v;
    return "md";
}

/** Derive a 1–2 character uppercase abbreviation from a record's display
    text — used for the empty-image placeholder so an unset tile still
    reads clearly (e.g. "Acme Corp" → "AC", "OPP-2026-000001" → "OP").
    Returns "" when no usable letters/digits are present (caller falls
    back to the image icon). */
export function computeInitials(text: string | null | undefined): string {
    if (!text) return "";
    // Split on whitespace and common separators (-, _, /, .) so coded
    // identifiers and multi-word names both yield sensible initials.
    const words = text
        .split(/[\s\-_/.]+/)
        .map((w) => w.trim())
        .filter(Boolean);
    if (words.length === 0) return "";
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}

/** Normalise the field value to a single attachment uuid string. The
    backend sometimes ships a bare string, sometimes a `{record_uuid}`
    object (when the read endpoint enriches the reference). */
export function extractAttachmentUuid(v: unknown): string | null {
    if (typeof v === "string" && v) return v;
    if (v && typeof v === "object") {
        const obj = v as Record<string, unknown>;
        const id = obj["record_uuid"] ?? obj["id"];
        if (typeof id === "string" && id) return id;
    }
    return null;
}

interface ImageTileProps {
    uuid: string | null;
    label: string;
    size: ImageFieldSize;
    // Record display text used to derive the placeholder initials when
    // no image is set (new record, or a record that never got one).
    // Falls back to the image icon when it yields no usable letters.
    fallbackText?: string;
}

function useBlobPreview(uuid: string | null): string | null {
    const runtime = useRuntime();
    const [url, setUrl] = useState<string | null>(null);
    useEffect(() => {
        if (!uuid) { setUrl(null); return; }
        let cancelled = false;
        let objectUrl: string | null = null;
        runtime.http.getBlob(`/api/document/attachments/${encodeURIComponent(uuid)}/preview`)
            .then((blob) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setUrl(objectUrl);
            })
            .catch(() => {
                if (!cancelled) setUrl(null);
            });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [runtime, uuid]);
    return url;
}

/** Read-mode tile — shown wherever a fields.Image value is displayed
    (form sheet read mode, list cells with the image widget). When no
    value is set (or the preview fetch fails) it renders a clearly-visible
    placeholder: the record's initials when `fallbackText` yields any, or
    the image icon otherwise. */
export function ImageTile({ uuid, label, size, fallbackText }: ImageTileProps): ReactNode {
    const src = useBlobPreview(uuid);
    const tile = TILE_CLASSES[size];
    const icon = ICON_CLASSES[size];
    if (uuid && src) {
        return (
            <img
                src={src}
                alt={label}
                className={`${tile} object-cover rounded-[2px] image-tile`}
            />
        );
    }
    const initials = computeInitials(fallbackText);
    return (
        <div className={`${tile} rounded-[2px] image-tile__placeholder`} aria-label={label}>
            {initials ? (
                <span className={`image-tile__initials ${INITIALS_CLASSES[size]}`}>{initials}</span>
            ) : (
                <ImageIcon className={icon} />
            )}
        </div>
    );
}

interface ImageFieldEditorProps {
    value: unknown;
    onChange: (next: string | null) => void;
    modelKey: string;
    recordUuid: string;
    fieldName: string;
    label: string;
    disabled?: boolean;
    size?: ImageFieldSize;
    // Record display text → placeholder initials when no image is set.
    fallbackText?: string;
}

/** Edit-mode renderer — the tile + a pencil-replace + trash-clear
    icon button. Disabled when the consumer record is unsaved
    (modelKey + recordUuid both required to land an attachment row). */
export function ImageFieldEditor({
    value,
    onChange,
    modelKey,
    recordUuid,
    fieldName,
    label,
    disabled,
    size = "md",
    fallbackText,
}: ImageFieldEditorProps): ReactNode {
    const runtime = useRuntime();
    const inputRef = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const uuid = extractAttachmentUuid(value);
    const canUpload = Boolean(modelKey && recordUuid) && !disabled;

    const handleFile = async (file: File): Promise<void> => {
        setBusy(true);
        setError(null);
        try {
            const qs = new URLSearchParams({
                model_key: modelKey,
                record_uuid: recordUuid,
                field_name: fieldName,
                usage_kind: "field_image",
                filename: file.name,
                mimetype: file.type || "application/octet-stream",
            });
            const buf = await file.arrayBuffer();
            const body = await runtime.http.postBytes<{ success?: boolean; error?: string; attachment?: { record_uuid?: string } }>(
                `/api/document/attachments?${qs.toString()}`,
                buf,
                file.type || "application/octet-stream",
            );
            if (!body?.success) {
                setError(body?.error || "Upload failed");
                return;
            }
            const newUuid = body.attachment?.record_uuid;
            if (typeof newUuid === "string" && newUuid) onChange(newUuid);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Upload failed");
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <div className="image-field">
            <div className={`relative ${TILE_CLASSES[size]} group`}>
                <ImageTile uuid={uuid} label={label} size={size} fallbackText={fallbackText} />
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleFile(f);
                    }}
                />
                {canUpload ? (
                    <>
                        <button
                            type="button"
                            aria-label={uuid ? "Replace image" : "Upload image"}
                            onClick={() => inputRef.current?.click()}
                            disabled={busy}
                            className="image-field__btn image-field__btn--upload"
                        >
                            <Pencil className="w-3 h-3" />
                        </button>
                        {uuid ? (
                            <button
                                type="button"
                                aria-label="Remove image"
                                onClick={() => onChange(null)}
                                disabled={busy}
                                className="image-field__btn image-field__btn--clear"
                            >
                                <Trash2 className="w-3 h-3" />
                            </button>
                        ) : null}
                    </>
                ) : null}
            </div>
            {busy ? <p className="image-field__status">Uploading…</p> : null}
            {!canUpload && !disabled ? (
                <p className="image-field__status">Save first to upload.</p>
            ) : null}
            {error ? <p className="image-field__status image-field__status--error">{error}</p> : null}
        </div>
    );
}
